import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Button,
  Message,
  Space,
  Card
} from '@arco-design/web-react';
import { IconUser, IconPlus, IconDelete } from '@arco-design/web-react/icon';
import { useSaveCorporateOperator } from '../hooks/useOnboardingData';
import './TeacherDetailsStep.css';
import ReportProblemButton from '../components/ReportProblemButton';

const FormItem = Form.Item;

function CorporateOperatorStep({
  onNext,
  onBack,
  initialData,
  requestedChanges,
  previewMode = false,
  freeMode = false
}) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();
  const saveMutation = useSaveCorporateOperator();
  const [operators, setOperators] = useState([
    { full_name: '', email: '', phone: '' }
  ]);

  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'operator' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  useEffect(() => {
    if (initialData?.operator_contacts && initialData.operator_contacts.length > 0) {
      setOperators(initialData.operator_contacts);
    }
  }, [initialData]);

  const validateOperators = () => {
    const invalid = operators.find((op) => !op.full_name || !op.email || !op.phone);
    if (invalid) {
      Message.error(t('steps.corporateOperator.messages.completeOperatorData'));
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    operator_contacts: operators
  });

  const handleSubmit = async () => {
    if (!validateOperators()) return;

    if (previewMode) {
      Message.success(t('steps.corporateOperator.messages.simulationValidated'));
      onNext(null);
      return;
    }

    saveMutation.mutate(buildPayload(), {
      onSuccess: () => {
        Message.success(t('steps.corporateOperator.messages.operatorsSaved'));
        onNext(null);
      },
      onError: () => {
        Message.error(t('shared.messages.errorSaving'));
      }
    });
  };

  const handleSaveDraft = async () => {
    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }
    if (!validateOperators()) return;

    saveMutation.mutate(buildPayload(), {
      onSuccess: () => {
        Message.success(t('shared.messages.dataSavedDraft'));
      },
      onError: () => {
        Message.error(t('shared.messages.errorSavingDraft'));
      }
    });
  };

  const addOperator = () => {
    setOperators([...operators, { full_name: '', email: '', phone: '' }]);
  };

  const removeOperator = (index) => {
    if (operators.length > 1) {
      setOperators(operators.filter((_, i) => i !== index));
    }
  };

  const updateOperator = (index, field, value) => {
    const updated = [...operators];
    updated[index] = { ...updated[index], [field]: value };
    setOperators(updated);
  };

  return (
    <div className="teacher-details-step">
      <div className="step-header">
        <h2>{t('steps.corporateOperator.title')}</h2>
        <p>{t('steps.corporateOperator.description')}</p>
      </div>

      <Form form={form} layout="vertical" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>
            <IconUser style={{ marginRight: 8 }} />
            {t('steps.corporateOperator.labels.operators')}
          </h3>

          {operators.map((op, index) => (
            <Card
              key={index}
              style={{ marginBottom: 16 }}
              title={t('steps.corporateOperator.labels.operatorTitle', { number: index + 1 })}
              extra={
                operators.length > 1 && (
                  <Button
                    type="text"
                    status="danger"
                    icon={<IconDelete />}
                    onClick={() => removeOperator(index)}
                  >
                    {t('shared.labels.delete')}
                  </Button>
                )
              }
            >
              <FormItem
                label={t('steps.corporateOperator.labels.fullName')}
                required
                {...getFieldProps(`operator_contacts.${index}.full_name`)}
              >
                <Input
                  placeholder={t('steps.corporateOperator.placeholders.fullName')}
                  value={op.full_name}
                  onChange={(value) => updateOperator(index, 'full_name', value)}
                />
              </FormItem>

              <FormItem
                label={t('steps.corporateOperator.labels.corporateEmail')}
                required
                {...getFieldProps(`operator_contacts.${index}.email`)}
              >
                <Input
                  placeholder={t('steps.corporateOperator.placeholders.email')}
                  value={op.email}
                  onChange={(value) => updateOperator(index, 'email', value)}
                />
              </FormItem>

              <FormItem
                label={t('shared.labels.phone')}
                required
                {...getFieldProps(`operator_contacts.${index}.phone`)}
              >
                <Input
                  placeholder={t('steps.corporateOperator.placeholders.phone')}
                  value={op.phone}
                  onChange={(value) => updateOperator(index, 'phone', value)}
                />
              </FormItem>
            </Card>
          ))}

          <Button type="dashed" long icon={<IconPlus />} onClick={addOperator}>
            {t('shared.buttons.addOperator')}
          </Button>
        </div>

        <div className="form-actions">
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button size="large" onClick={onBack}>
              {t('shared.buttons.back')}
            </Button>
            <Button
              size="large"
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              className="onboarding-draft-button"
            >
              {t('shared.buttons.saveDraft')}
            </Button>
            <Button type="primary" size="large" htmlType="submit" loading={saveMutation.isPending}>
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button size="large" onClick={() => onNext(null)}>
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <ReportProblemButton stepLabel={t('steps.corporateOperator.title')} previewMode={previewMode} asButton />
          </Space>
        </div>
      </Form>
    </div>
  );
}

export default CorporateOperatorStep;
